"use client";

import Link from "next/link";
import { ListChecks, Cable, ShieldCheck } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";

const CARD_ACCENTS = [
  { icon: ListChecks, color: "var(--terracotta)", tint: "rgba(184,83,26,0.10)" },
  { icon: Cable, color: "var(--ink-soft)", tint: "rgba(26,24,21,0.06)" },
  { icon: ShieldCheck, color: "var(--moss)", tint: "rgba(42,74,59,0.10)" },
];

// Minimalist integration marks (reused from the integrations strip) — shown
// inside the second Bento card.
const INTEGRATION_LOGOS = [
  <svg width="30" height="30" viewBox="0 0 36 36" fill="none" key="todoist" aria-label="Todoist">
    <rect x="3" y="3" width="30" height="30" rx="6" fill="#E44332" />
    <path d="M11 17l3 3 7-8" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="30" height="30" viewBox="0 0 36 36" fill="none" key="cal" aria-label="Google Calendar">
    <rect x="4" y="4" width="28" height="28" rx="4" fill="#FFFFFF" stroke="#1A1815" strokeWidth="1.4" />
    <rect x="4" y="4" width="28" height="7" rx="4" fill="#1A1815" />
    <rect x="4" y="9" width="28" height="2" fill="#1A1815" />
    <text x="18" y="25" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="12" fontWeight="500" fill="#1A1815">20</text>
  </svg>,
  <svg width="30" height="30" viewBox="0 0 36 36" fill="none" stroke="var(--ink)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" key="mail" aria-label="Email">
    <rect x="4" y="8" width="28" height="20" rx="2" />
    <path d="M4 10l14 11L32 10" />
  </svg>,
];

export function ToolkitSection() {
  const { t } = useTranslation();
  const tk = t.landing.cover.toolkit;

  return (
    <section className="relative max-w-[1320px] mx-auto px-5 md:px-12 py-20 md:py-[120px] z-[2]">
      <div className="max-w-[760px] mx-auto text-left md:text-center">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {tk.kicker}
        </div>
        <h2
          className="font-display text-[clamp(32px,4vw,52px)] leading-[1.05] tracking-[-0.02em] text-balance"
          style={{ fontWeight: 380 }}
        >
          {tk.h2a} <em className="text-[var(--terracotta)] italic">{tk.h2em}</em>
        </h2>
      </div>

      <div className="mt-10 md:mt-14 grid gap-4 md:gap-6 md:grid-cols-3 max-w-[1120px] mx-auto">
        {tk.cards.map((card, i) => {
          const accent = CARD_ACCENTS[i] ?? CARD_ACCENTS[0];
          const Icon = accent.icon;
          const isIntegrations = i === 1;
          return (
            <div
              key={card.title}
              className="flex flex-col rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/30 p-8"
            >
              <span
                className="mb-5 grid h-11 w-11 place-items-center rounded-xl"
                style={{ background: accent.tint, color: accent.color }}
              >
                <Icon className="h-5 w-5" />
              </span>
              <h3
                className="font-display text-[clamp(21px,1.9vw,26px)] leading-[1.15] tracking-[-0.01em] text-[var(--ink)]"
                style={{ fontWeight: 400 }}
              >
                {card.title}
              </h3>
              <p className="mt-3 text-[15px] leading-[1.6] text-[var(--ink-soft)] text-pretty">
                {card.body}
              </p>

              {isIntegrations && (
                <div className="mt-auto flex items-center gap-3.5 pt-6">
                  {INTEGRATION_LOGOS.map((logo) => (
                    <span
                      key={logo.key}
                      className="grid h-11 w-11 place-items-center rounded-xl border border-[var(--rule)] bg-[var(--paper)]"
                    >
                      {logo}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 md:mt-14 flex md:justify-center">
        <Link
          href="/product/integrations"
          className="inline-flex w-fit items-center font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)] underline-offset-4 hover:underline"
        >
          {`${tk.learnMore} →`}
        </Link>
      </div>
    </section>
  );
}
